import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtCVE } from "@/lib/format";
import { fileUrl } from "@/lib/site";
import { ImageUpload } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, ForkKnife, CircleNotch, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", price: 0, photo_id: null, category: "", visible: true, position: 0 };

export default function SiteMenu() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/site/menu");
      setItems(data.items);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = [...new Set(items.map((i) => i.category).filter(Boolean))];
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) return toast.error("O nome é obrigatório");
    setBusy(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, position: Number(form.position) || 0 };
      if (editing) await api.put(`/site/menu/${editing.id}`, payload);
      else await api.post("/site/menu", payload);
      toast.success(editing ? "Item atualizado" : "Item criado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (p) => {
    try {
      await api.put(`/site/menu/${p.id}`, { ...p, visible: !p.visible });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminar "${p.name}"?`)) return;
    try {
      await api.delete(`/site/menu/${p.id}`);
      toast.success("Item eliminado");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="site-menu-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Site público</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Carta / Produtos</h1>
          <p className="mt-1 text-sm text-muted-foreground">Os itens visíveis aparecem automaticamente no seu site.</p>
        </div>
        <Button className="rounded-full" onClick={openNew} data-testid="new-menu-item-btn"><Plus size={18} className="mr-1.5" /> Novo item</Button>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card py-16 text-center">
          <ForkKnife size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground">A sua carta ainda está vazia.</p>
          <Button variant="outline" className="rounded-full" onClick={openNew}>Adicionar primeiro item</Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => (
            <div key={p.id} className={`stagger-item flex gap-3 rounded-xl border border-border bg-card p-3 ${p.visible ? "" : "opacity-60"}`}
              data-testid={`menu-item-${p.id}`}>
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-muted">
                {p.photo_id ? <img src={fileUrl(p.photo_id)} alt="" className="h-full w-full object-cover" loading="lazy" />
                  : <div className="flex h-full items-center justify-center text-muted-foreground"><ForkKnife size={22} /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{p.name}</div>
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{p.category || "Sem categoria"}</div>
                  </div>
                  <div className="whitespace-nowrap text-sm font-bold">{fmtCVE(p.price)}</div>
                </div>
                <div className="mt-2 flex items-center justify-end gap-0.5">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggle(p)} title={p.visible ? "Ocultar" : "Mostrar"}
                    data-testid={`toggle-menu-item-${p.id}`}>{p.visible ? <Eye size={16} /> : <EyeSlash size={16} />}</Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-menu-item-${p.id}`}><PencilSimple size={16} /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(p)} data-testid={`delete-menu-item-${p.id}`}><Trash size={16} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="menu-item-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar item" : "Novo item"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-[110px_1fr] gap-3">
            <ImageUpload value={form.photo_id} onChange={(v) => setForm({ ...form, photo_id: v })} testid="menu-item-photo" label="Foto" />
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Nome *</Label><Input value={form.name} onChange={set("name")} data-testid="menu-item-name" /></div>
              <div className="space-y-1.5"><Label>Preço (CVE)</Label>
                <Input type="number" min="0" step="1" value={form.price} onChange={set("price")} data-testid="menu-item-price" /></div>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Categoria</Label>
              <Input list="menu-cats" value={form.category} onChange={set("category")} placeholder="Ex.: Entradas, Pratos, Bebidas" data-testid="menu-item-category" />
              <datalist id="menu-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Descrição</Label>
              <Textarea value={form.description} onChange={set("description")} data-testid="menu-item-description" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <Switch checked={form.visible} onCheckedChange={(v) => setForm({ ...form, visible: v })} data-testid="menu-item-visible" /> Visível no site
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={busy} data-testid="save-menu-item-btn">{busy ? <CircleNotch className="animate-spin" /> : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
